'use client'
import { onTrasitionEnd } from '@/lib/transition'
import clsx from 'clsx'
import { useEffect, useRef, useState, type ComponentPropsWithRef, type CSSProperties, type ElementType } from 'react'

type DrawerDirection = 'horizontal' | 'vertical'

type DrawerProps<T extends ElementType> = ComponentPropsWithRef<T> & {
    open?: boolean
    as?: T
    direction?: DrawerDirection
    openClass?: string
    onOpen?: () => void
    closedClass?: string
    onClose?: () => void
}

const Drawer = <T extends ElementType>(props: DrawerProps<T>) => {
    const {
        children,
        className,
        open = true,
        as: Component = 'div',
        ref,
        direction = 'vertical',
        closedClass,
        openClass,
        onOpen,
        onClose,
        ...otherProps
    } = props
    const drawerRef = useRef<HTMLElement>(null)

    const initialStyle = direction === 'vertical' ? { height: 0, display: 'none' } : { width: 0, display: 'none' }
    const [currentStyle, setCurrentStyle] = useState<CSSProperties>(open ? {} : initialStyle)
    const [isOpen, setIsOpen] = useState(open)

    useEffect(() => {
        if (typeof ref === 'function') {
            ref(drawerRef.current)
        } else if (ref) {
            ref.current = drawerRef.current
        }
    }, [ref, drawerRef])

    useEffect(() => {
        if (!drawerRef.current) return

        if (open && !isOpen) {
            setCurrentStyle(direction === 'vertical' ? { height: 0 } : { width: 0 })

            setTimeout(() => {
                if (!drawerRef.current) return

                onTrasitionEnd(drawerRef.current, () => {
                    setCurrentStyle({})
                    setIsOpen(true)
                    if (onOpen) onOpen()
                })

                setCurrentStyle(
                    direction === 'vertical'
                        ? { height: drawerRef.current.scrollHeight }
                        : { width: drawerRef.current.scrollWidth },
                )
            }, 10)
        } else if (!open && isOpen) {
            onTrasitionEnd(drawerRef.current, () => {
                setCurrentStyle(
                    direction === 'vertical' ? { height: 0, display: 'none' } : { width: 0, display: 'none' },
                )
                setIsOpen(false)
                if (onClose) onClose()
            })

            setCurrentStyle(
                direction === 'vertical'
                    ? { height: drawerRef.current.scrollHeight }
                    : { width: drawerRef.current.scrollWidth },
            )

            setTimeout(() => {
                setCurrentStyle(direction === 'vertical' ? { height: 0 } : { width: 0 })
            }, 10)
        }
    }, [open, direction, onOpen, onClose, isOpen])

    return (
        <Component
            className={clsx('overflow-hidden transition-all', open ? openClass : closedClass, className)}
            aria-hidden={!isOpen}
            style={currentStyle}
            ref={drawerRef}
            {...otherProps}
        >
            {children}
        </Component>
    )
}

Drawer.displayName = 'Drawer'

export { Drawer }
